/**
 * Explicit, independent version numbers (D-027, D-023).
 *
 * These move separately. A change to the database structure does not force an
 * export-format bump, and vice versa.
 */

/**
 * IndexedDB structure and record shape. Integer, forward-only.
 *
 * **Bumped to 4 at Version 2's C7.5** (D-105 §12), adding `trainingWeeks` — the
 * materialised week the recurring schedule is frozen into. Additive, and with
 * **no backfill at all**: an existing project gains no schedule and no
 * historical weeks, because a week the user never saw must not be manufactured
 * so that it can be reported as empty, and deriving a weekday for an existing
 * commitment would be a number from nowhere (D-101 §3). The schedule itself
 * needs no store — it is an optional field on records that are not rewritten.
 *
 * **Bumped to 3 at Version 2's C6.0** (D-092), adding the seven stores the
 * opt-in domain model needs: `domainGoals`, `nutritionPlans`, `dailyChecks`,
 * `routineAnchors`, `wearableImports`, `aiProposals` and `connections`. Additive
 * only — no existing record is rewritten, which is what makes this migration
 * ordinary rather than the exceptional one version 2 was. Four of the seven are
 * created here and modelled in their own later checkpoints, exactly as the v1
 * schema did for the weekly-evidence stores.
 *
 * **Bumped to 2 at Checkpoint 2.5**, adding the `projects` store and a
 * `projectId` foreign key on phases, goals and weekly reviews (D-062). This is
 * the first version change since the initial schema, and the first migration
 * that runs against a database holding real data rather than a fresh install.
 */
export const DB_SCHEMA_VERSION = 4

/**
 * AI-review JSON contract.
 *
 * **Bumped to 5 at C6.5 phase 3** (D-099, as amended): each weekly review gained
 * a `planSnapshot` — the active domains, each goal's intention, priority and its
 * standard and minimum-viable weeks, and the nutrition plan source and targets
 * in force. **This is the first contract change since D-082**, and it is a real
 * one: a reader can now tell what the user was actually asked to do that week,
 * which no previous version could express.
 *
 * The snapshot's routine anchors are **deliberately omitted from the document**
 * (D-099 §B), unconditionally rather than behind a privacy flag. The field is
 * stored on the record for historical UI and has no business in an AI-review
 * file. The gate lives in `serializeWeeklyReport` and nowhere else.
 *
 * **Bumped to 4 at Checkpoint 4.1** (D-082): the `photos` block became real.
 * It had been `excluded` unconditionally since version 1, with a reason that was
 * true while no photo could exist and became false the moment Checkpoint 3
 * shipped capture — the same lag the version-2 blocks had. It carries metadata
 * only, never bytes, plus the `mediaFile` name that links each entry to the
 * optional media ZIP.
 *
 * **Bumped to 3 at the roadmap's Checkpoint 2.5d** (D-062): the weekly document
 * gained a `project` block — name, outcome, week N of M, target date and the
 * route Project Sola recommended — and the project completion summary became
 * its own export with its own contract.
 *
 * **Bumped to 2 at Checkpoint 2.7**, which was also a real contract change: the
 * document gained recovery, adherence, strength, projection, phase-history and
 * per-week opinion blocks, each replacing a `missing` placeholder.
 *
 * **Every earlier golden file is retained** beside the current one (D-039), so
 * a reader holding an older export can still see the shape it was written
 * against. Nothing is replaced.
 *
 * The three content corrections at 2.3, 2.4 and sub-checkpoint 2.5 deliberately
 * did NOT bump this: same fields, same meanings, same units, corrected wording.
 * Neither did the database reaching schema version 2, which moves independently.
 *
 * **Bumped to 6 at C7.5 phase F** (D-105 §12): the plan snapshot's goals carry
 * the recurring schedule, and every finalized week carries the sessions it
 * actually contained. **The `adherence` block did not change shape** — its two
 * training counts are derived from the materialised week now rather than typed,
 * which is a change in provenance and not in contract, so nothing there moved.
 */
export const EXPORT_FORMAT_VERSION = 6

/** Backup container and manifest layout. */
export const BACKUP_FORMAT_VERSION = 1

/**
 * Algorithm identifiers.
 *
 * These are NEVER mutated in place. A behaviour change means a new identifier
 * (`..._v2`), so a stored snapshot always says which algorithm produced it and
 * remains interpretable later. This is what makes D-023's "never make an opaque
 * recommendation that cannot be explained later" enforceable.
 */
export const ALGORITHM_IDS = {
  bodyFat: 'male_navy_circumference_v1',
  trend: 'trend_v1',
  rules: 'rules_v1',
  /* The route-recommendation engine — the second deterministic engine, approved
     in D-062 with its mapping and thresholds in D-071. Separate from `rules`
     because it answers a different question at a different moment: which route
     to take at setup, rather than how this week went. A recommendation snapshot
     is stored immutably on the project, so it must say which logic produced it;
     a change in what the same condition and outcome yield means `route_v2`. */
  route: 'route_v1',
  /* Epley — `load × (1 + reps / 30)` — approved in D-069, closing L-002. Only
     sets of ten reps or fewer produce an estimate, and a lift's weekly figure
     is the highest among its qualifying sets. Implemented at Checkpoint 2.2
     (D-074) in `calculations/estimated1RM.ts`, computed on read from the raw
     sets so nothing derived is stored and a later `_v2` reinterprets the same
     history. Applied literally, a one-rep set reports `load × 31/30`; whether
     it should instead report the load itself is open as L-015, and a change
     there means a new identifier, never an edit here. */
  estimated1RM: 'estimated_1rm_epley_v1',
  /* Goal projection — approved in D-068, closing L-001. Compounds the weekly
     rate against the running weight rather than extrapolating linearly, because
     the rate is a percentage of a changing body weight. The three plan paths
     read the phase's own stored range, so the identifier covers the method and
     not the numbers: a user who edits their range gets a different projection
     from the same algorithm, which is the intent. A change to the stepping
     method means `_v2`, never an edit here. */
  projection: 'projection_compounding_v1',
} as const

export type AlgorithmId = (typeof ALGORITHM_IDS)[keyof typeof ALGORITHM_IDS]
